import { LoadingOutlined } from '@ant-design/icons';
import { Tooltip } from '@gilbarbara/components';
import AnimatingDots from '~/components/AnimatingDots';
import Button from '~/components/Button';
import Divider from '~/components/Divider';
import DropdownInput from '~/components/DropdownInput';
import Gap from '~/components/Gap';
import Header from '~/components/Header';
import StyledInput from '~/components/StyledInput';
import StyledTextArea from '~/components/StyledTextArea';
import Text from '~/components/Text';
import {
  COUNTRIES,
  COUNTRIES_CODES,
  getCountryCodeByCountryName,
  getCurrencyByCountry,
} from '~/constants/address';
import InfoIcon from '~/icons/InfoIcon';
import { useAddBusiness } from '~/routes/Auth/AccountSetup/useAddBusiness';
import { useDispatch } from '~/store';
import LogoSection from '../Business/LogoSection';
import TemplateSection from '../Business/TemplateSection';
import OptionSelector from '../AddInvoice/InvoiceForm/OptionSelector';
import * as Styles from '../Business/styles';
import { logOut } from '~/actions';
import { useIsDesktop } from '~/hooks/useIsDesktop';
import { useNavigate } from 'react-router-dom';

const AddBusiness = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const { business, isSaving, onChange, isLoading, saveBusiness } = useAddBusiness({});
  const dispatch = useDispatch();

  const handleClickLogout = () => {
    dispatch(logOut());
  };

  const handleSaveBusiness = async () => {
    await saveBusiness();
    // Navigate to business settings or dashboard after successful creation
    navigate('/private/business');
  };

  if (isLoading) {
    return (
      <div className="full-dimensions center">
        <AnimatingDots />
      </div>
    );
  }

  return (
    <div>
      <Header headerText="Add New Business" />
      <Styles.Section>
        <Styles.SectionDesc>
          <Text className={isDesktop ? 'bold' : ''} size={isDesktop ? 'sm' : 'ssmd'}>
            Basic Details
          </Text>
          <Gap t={4} />
          <Text size="xs" variant="neutral">
            Enter contact person's name and company name
          </Text>
        </Styles.SectionDesc>
        <Gap r={40} />

        <div>
          <div className="center">
            <div className="full-flex">
              <Text size="xs">
                First Name <span className="danger">*</span>
              </Text>
              <Gap t={4} />
              <StyledInput
                value={business.first_name}
                onChange={e => onChange({ first_name: e.target.value })}
                className={`${!business.first_name && 'error-field'}`}
              />
            </div>
            <Gap r={16} />
            <div className="full-flex">
              <Text size="xs">
                Last Name <span className="danger">*</span>
              </Text>
              <Gap t={4} />
              <StyledInput
                value={business.last_name}
                onChange={e => onChange({ last_name: e.target.value })}
                className={`${!business.last_name && 'error-field'}`}
              />
            </div>
          </div>

          <Gap t={16} />
          <div className="center">
            <div className="full-flex">
              <Text size="xs">
                Company Name <span className="danger">*</span>
              </Text>
              <Gap t={4} />
              <StyledInput
                value={business.company_name}
                onChange={e => onChange({ company_name: e.target.value })}
                className={`${!business.company_name && 'error-field'}`}
              />
            </div>
            <Gap r={16} />
            <div className="full-flex">
              <Text size="xs">Website URL</Text>
              <Gap t={4} />
              <StyledInput
                value={business.website}
                onChange={e => onChange({ website: e.target.value })}
              />
            </div>
          </div>

          <Gap t={16} />
          <div className="center">
            <div className="full-flex">
              <div className="align-center">
                <Text size="xs">
                  Business Email <span className="danger">*</span>
                </Text>
                <Gap r={8} />
                <Tooltip content="This is different from you account email, and shows up in the invoice">
                  <InfoIcon className="cursor-pointer" />
                </Tooltip>
              </div>

              <Gap t={4} />
              <StyledInput
                value={business.email}
                onChange={e => onChange({ email: e.target.value })}
                className={`${!business.email && 'error-field'}`}
                name="email"
              />
            </div>
          </div>

          <Gap t={16} />
          <div className="center">
            <div className="full-flex">
              <Text size="xs">Country Code</Text>
              <Gap t={4} />
              <DropdownInput
                search
                value={business.country_code || ''}
                onChange={value => {
                  onChange({ country_code: value });
                }}
                options={COUNTRIES_CODES}
                showSelection={false}
              />
            </div>
            <Gap r={16} />
            <div className="full-flex">
              <Text size="xs">Phone</Text>
              <Gap t={4} />
              <StyledInput
                value={business.phone}
                onChange={e => onChange({ phone: e.target.value })}
              />
            </div>
          </div>
        </div>
      </Styles.Section>

      <Divider />

      <Styles.Section>
        <Styles.SectionDesc>
          <Text className={isDesktop ? 'bold' : ''} size={isDesktop ? 'sm' : 'ssmd'}>
            Address Details
          </Text>
          <Gap t={4} />
          <Text size="xs" variant="neutral">
            Enter Company's Address details
          </Text>
        </Styles.SectionDesc>
        <Gap r={40} />

        <div>
          <Text size="xs">
            Country <span className="danger">*</span>
          </Text>
          <Gap t={4} />
          <DropdownInput
            search
            value={business.country}
            className={`${!business.country && 'error-field'}`}
            onChange={value => {
              onChange({ country: value });
              if (!business.currency) {
                const countryCurrency = getCurrencyByCountry(value);
                onChange({ currency: countryCurrency });
              }
              const countryCode = getCountryCodeByCountryName(value);
              if (countryCode) {
                onChange({ country_code: countryCode });
              }
            }}
            options={COUNTRIES}
          />

          <Gap t={16} />
          <Text size="xs">Address Line 1</Text>
          <Gap t={6} />
          <StyledInput
            value={business.address_line_1}
            onChange={e => onChange({ address_line_1: e.target.value })}
          />

          <Gap t={16} />

          <div className="center">
            <div className="full-flex">
              <Text size="xs">Address Line 2</Text>
              <Gap t={4} />
              <StyledInput
                value={business.address_line_2}
                onChange={e => onChange({ address_line_2: e.target.value })}
              />
            </div>
            <Gap r={16} />
            <div className="full-flex">
              <Text size="xs">City</Text>
              <Gap t={4} />
              <StyledInput
                value={business.city}
                onChange={e => onChange({ city: e.target.value })}
              />
            </div>
          </div>

          <Gap t={16} />
          <div className="center">
            <div className="full-flex">
              <Text size="xs">State / Province</Text>
              <Gap t={4} />
              <StyledInput
                value={business.state}
                onChange={e => onChange({ state: e.target.value })}
              />
            </div>
            <Gap r={16} />
            <div className="full-flex">
              <Text size="xs">Postal code</Text>
              <Gap t={4} />
              <StyledInput
                value={business.postal_code}
                onChange={e => onChange({ postal_code: e.target.value })}
              />
            </div>
          </div>
        </div>
      </Styles.Section>

      <Divider />

      <Styles.Section>
        <Styles.SectionDesc>
          <Text className={isDesktop ? 'bold' : ''} size={isDesktop ? 'sm' : 'ssmd'}>
            Tax, Shipping and Discount
          </Text>
          <Gap t={4} />
          <Text size="xs" variant="neutral">
            Add tax, shipping and discount details which will be used as default when creating the
            invoices
          </Text>
        </Styles.SectionDesc>
        <Gap r={40} />

        <div>
          <Text size="xs">Discount</Text>
          <Gap t={4} />
          <div className="full-width align-center">
            <StyledInput
              className="full-flex attachedInput"
              value={business.discount}
              onChange={(event: any) => {
                onChange({
                  discount: event.target.value,
                });
              }}
            />

            <OptionSelector
              value={business.discount_type}
              onChange={newVal => {
                onChange({
                  discount_type: newVal,
                });
              }}
            />
          </div>

          <Gap t={16} />
          <Text size="xs">Shipping</Text>
          <Gap t={4} />
          <div className="full-width align-center">
            <StyledInput
              className="full-flex attachedInput"
              value={business.shipping}
              onChange={(event: any) => {
                onChange({
                  shipping: event.target.value,
                });
              }}
            />

            <OptionSelector
              value={business.shipping_type}
              onChange={newVal => {
                onChange({
                  shipping_type: newVal,
                });
              }}
            />
          </div>

          <Gap t={16} />
          <Text size="xs">Tax</Text>
          <Gap t={4} />
          <div className="full-width align-center">
            <StyledInput
              className="full-flex attachedInput"
              value={business.tax}
              onChange={(event: any) => {
                onChange({
                  tax: event.target.value,
                });
              }}
            />

            <OptionSelector
              value={business.tax_type}
              onChange={newVal => {
                onChange({
                  tax_type: newVal,
                });
              }}
            />
          </div>
        </div>
      </Styles.Section>

      <Divider />

      <Styles.Section>
        <Styles.SectionDesc>
          <Text className={isDesktop ? 'bold' : ''} size={isDesktop ? 'sm' : 'ssmd'}>
            Terms and conditions
          </Text>
          <Gap t={4} />
          <Text size="xs" variant="neutral">
            Set default terms and conditions for your invoices
          </Text>
        </Styles.SectionDesc>
        <Gap r={40} />

        <div>
          <Text size="xs">Terms and conditions</Text>
          <Gap t={4} />
          <StyledTextArea
            value={business.terms}
            style={{
              width: '600px',
              maxWidth: '100%',
            }}
            onChange={(event: any) => {
              onChange({
                terms: event.target.value,
              });
            }}
            placeholder="Add your terms and conditions"
            rows={5}
          />
        </div>
      </Styles.Section>

      <Divider />

      <Styles.Section>
        <Styles.SectionDesc>
          <Text className={isDesktop ? 'bold' : ''} size={isDesktop ? 'sm' : 'ssmd'}>
            Notes
          </Text>
          <Gap t={4} />
          <Text size="xs" variant="neutral">
            Set default notes for your invoices
          </Text>
        </Styles.SectionDesc>
        <Gap r={40} />

        <div>
          <Text size="xs">Notes</Text>
          <Gap t={4} />
          <StyledTextArea
            value={business.notes}
            style={{
              width: '600px',
              maxWidth: '100%',
            }}
            onChange={(event: any) => {
              onChange({
                notes: event.target.value,
              });
            }}
            placeholder="Add your default notes"
            rows={5}
          />
        </div>
      </Styles.Section>

      <Divider />

      <TemplateSection onChange={onChange} business={business} />

      <Divider />

      <LogoSection onChange={onChange} business={business} />

      <Styles.Section />

      <Styles.FooterSection className="end-align-center">
        <Button
          disabled={isSaving || isLoading}
          onClick={handleSaveBusiness}
          className={isDesktop ? '' : 'full-width'}
          size={isDesktop ? 'md' : 'sm'}
        >
          {isSaving ? <LoadingOutlined /> : 'Create Business'}
        </Button>

        {!isDesktop && (
          <>
            <Gap r={8} />

            <Button
              size="sm"
              className="full-width"
              variant="secondary"
              onClick={handleClickLogout}
            >
              Log out
            </Button>
          </>
        )}
      </Styles.FooterSection>
    </div>
  );
};

export default AddBusiness;
